/**
 * ProcurementDetailsPanel
 *
 * Pure data mapping from ProcurementDetails to DataSheet sections.
 * For FPDS contracts only.
 */
import * as React from 'react'

import { isNil } from 'es-toolkit'
import { FileText } from 'lucide-react'

import {
  DataSheetFromData,
  type DataSheetRowData,
  type DataSheetSectionData,
  toTitleCase,
} from '@/components/opportunity/common'

import type { ProcurementDetails } from '@/types/usaspending'

// ============================================================================
// Data Mapping Functions
// ============================================================================

function buildCompetitionRows(data: ProcurementDetails): DataSheetRowData[] {
  const rows: DataSheetRowData[] = []

  if (!isNil(data.extentCompeted) || !isNil(data.extentCompetedDescription)) {
    rows.push({
      label: 'Extent Competed',
      value: toTitleCase(data.extentCompetedDescription),
      code: data.extentCompeted,
      highlight: data.extentCompeted === 'A',
    })
  }

  if (
    !isNil(data.otherThanFullAndOpenCompetition) ||
    !isNil(data.otherThanFullAndOpenCompetitionDescription)
  ) {
    rows.push({
      label: 'Other Than Full & Open',
      value: toTitleCase(data.otherThanFullAndOpenCompetitionDescription),
      code: data.otherThanFullAndOpenCompetition,
    })
  }

  if (
    !isNil(data.fairOpportunityLimitedSources) ||
    !isNil(data.fairOpportunityLimitedSourcesDescription)
  ) {
    rows.push({
      label: 'Fair Opportunity',
      value: toTitleCase(data.fairOpportunityLimitedSourcesDescription),
      code: data.fairOpportunityLimitedSources,
    })
  }

  return rows
}

function buildSolicitationRows(data: ProcurementDetails): DataSheetRowData[] {
  const rows: DataSheetRowData[] = []

  if (!isNil(data.solicitationIdentifier)) {
    rows.push({ label: 'Solicitation ID', value: data.solicitationIdentifier })
  }

  if (!isNil(data.solicitationDate)) {
    rows.push({ label: 'Solicitation Date', value: data.solicitationDate })
  }

  if (
    !isNil(data.solicitationProcedures) ||
    !isNil(data.solicitationProceduresDescription)
  ) {
    rows.push({
      label: 'Procedures',
      value: toTitleCase(data.solicitationProceduresDescription),
      code: data.solicitationProcedures,
    })
  }

  if (!isNil(data.numberOfOffersReceived)) {
    rows.push({
      label: 'Offers Received',
      value: String(data.numberOfOffersReceived),
    })
  }

  return rows
}

function buildPricingRows(data: ProcurementDetails): DataSheetRowData[] {
  const rows: DataSheetRowData[] = []

  if (
    !isNil(data.typeOfContractPricing) ||
    !isNil(data.typeOfContractPricingDescription)
  ) {
    rows.push({
      label: 'Contract Pricing Type',
      value: toTitleCase(data.typeOfContractPricingDescription),
      code: data.typeOfContractPricing,
    })
  }

  if (
    !isNil(data.costOrPricingData) ||
    !isNil(data.costOrPricingDataDescription)
  ) {
    rows.push({
      label: 'Cost or Pricing Data',
      value: toTitleCase(data.costOrPricingDataDescription),
      code: data.costOrPricingData,
    })
  }

  return rows
}

function buildCommercialSetAsideRows(
  data: ProcurementDetails,
): DataSheetRowData[] {
  const rows: DataSheetRowData[] = []

  if (!isNil(data.typeSetAside) || !isNil(data.typeSetAsideDescription)) {
    rows.push({
      label: 'Set Aside Type',
      value: toTitleCase(data.typeSetAsideDescription),
      code: data.typeSetAside,
      highlight: !!data.typeSetAside && data.typeSetAside !== 'NONE',
    })
  }

  if (
    !isNil(data.commercialItemAcquisition) ||
    !isNil(data.commercialItemAcquisitionDescription)
  ) {
    rows.push({
      label: 'Commercial Acquisition',
      value: toTitleCase(data.commercialItemAcquisitionDescription),
      code: data.commercialItemAcquisition,
    })
  }

  if (
    !isNil(data.commercialItemTestProgram) ||
    !isNil(data.commercialItemTestProgramDescription)
  ) {
    rows.push({
      label: 'Test Program',
      value: toTitleCase(data.commercialItemTestProgramDescription),
      code: data.commercialItemTestProgram,
    })
  }

  return rows
}

function buildContractCharacteristicsRows(
  data: ProcurementDetails,
): DataSheetRowData[] {
  const rows: DataSheetRowData[] = []

  if (
    !isNil(data.multiYearContract) ||
    !isNil(data.multiYearContractDescription)
  ) {
    rows.push({
      label: 'Multi-Year',
      value: toTitleCase(data.multiYearContractDescription),
      code: data.multiYearContract,
    })
  }

  if (
    !isNil(data.consolidatedContract) ||
    !isNil(data.consolidatedContractDescription)
  ) {
    rows.push({
      label: 'Consolidated',
      value: toTitleCase(data.consolidatedContractDescription),
      code: data.consolidatedContract,
    })
  }

  if (
    !isNil(data.purchaseCardAsPaymentMethod) ||
    !isNil(data.purchaseCardAsPaymentMethodDescription)
  ) {
    rows.push({
      label: 'Purchase Card',
      value: toTitleCase(data.purchaseCardAsPaymentMethodDescription),
      code: data.purchaseCardAsPaymentMethod,
    })
  }

  return rows
}

function buildComplianceRows(data: ProcurementDetails): DataSheetRowData[] {
  const rows: DataSheetRowData[] = []

  if (!isNil(data.laborStandards) || !isNil(data.laborStandardsDescription)) {
    rows.push({
      label: 'Labor Standards',
      value: toTitleCase(data.laborStandardsDescription),
      code: data.laborStandards,
    })
  }

  if (
    !isNil(data.constructionWageRateRequirement) ||
    !isNil(data.constructionWageRateRequirementDescription)
  ) {
    rows.push({
      label: 'Wage Rate Requirement',
      value: toTitleCase(data.constructionWageRateRequirementDescription),
      code: data.constructionWageRateRequirement,
    })
  }

  if (
    !isNil(data.domesticOrForeignEntity) ||
    !isNil(data.domesticOrForeignEntityDescription)
  ) {
    rows.push({
      label: 'Entity Origin',
      value: toTitleCase(data.domesticOrForeignEntityDescription),
      code: data.domesticOrForeignEntity,
    })
  }

  if (!isNil(data.foreignFunding) || !isNil(data.foreignFundingDescription)) {
    rows.push({
      label: 'Foreign Funding',
      value: toTitleCase(data.foreignFundingDescription),
      code: data.foreignFunding,
    })
  }

  if (
    !isNil(data.interagencyContractingAuthority) ||
    !isNil(data.interagencyContractingAuthorityDescription)
  ) {
    rows.push({
      label: 'Interagency Authority',
      value: toTitleCase(data.interagencyContractingAuthorityDescription),
      code: data.interagencyContractingAuthority,
    })
  }

  return rows
}

// ============================================================================
// Main Transform
// ============================================================================

function buildProcurementSections(
  data: ProcurementDetails,
): DataSheetSectionData[] {
  return [
    { title: 'Competition & Sources', rows: buildCompetitionRows(data) },
    { title: 'Solicitation', rows: buildSolicitationRows(data) },
    { title: 'Pricing & Cost', rows: buildPricingRows(data) },
    {
      title: 'Commercial & Set-Aside',
      rows: buildCommercialSetAsideRows(data),
    },
    {
      title: 'Contract Characteristics',
      rows: buildContractCharacteristicsRows(data),
    },
    { title: 'Compliance & Origin', rows: buildComplianceRows(data) },
  ]
}

function hasProcurementData(
  data: ProcurementDetails | null | undefined,
): boolean {
  if (!data) return false
  return !!(
    data.extentCompeted ||
    data.typeSetAside ||
    data.solicitationIdentifier ||
    data.typeOfContractPricing ||
    data.commercialItemAcquisition ||
    data.multiYearContract ||
    data.laborStandards ||
    data.domesticOrForeignEntity ||
    data.interagencyContractingAuthority
  )
}

// ============================================================================
// Component
// ============================================================================

interface ProcurementDetailsPanelProps {
  data: ProcurementDetails
}

export const ProcurementDetailsPanel: React.FC<
  ProcurementDetailsPanelProps
> = ({ data }) => {
  if (!hasProcurementData(data)) return null

  return (
    <DataSheetFromData
      title="Procurement Details"
      icon={<FileText />}
      badge={data.solicitationIdentifier}
      sections={buildProcurementSections(data)}
    />
  )
}

export { hasProcurementData }
